import { EmbedWrapper } from '@embeds/components/Layouts/EmbedWrapper';
import { TopHeadlines } from '@embeds/components/TopHeadlines';
import {
  rdbV1TrendingListsList,
  type TrendingListItemResponseTrendingListResponseRelatedPagedData,
} from '@on3/api';
import { getFulfilledValue, proxyApi } from '@on3/ui-lib/utils/api';
import { getIdFromParam } from '@on3/ui-lib/utils/url';
import type { GetServerSideProps } from 'next';
import Head from 'next/head';

export interface TopTenProps {
  trendingList: TrendingListItemResponseTrendingListResponseRelatedPagedData | null;
  mode: 'light' | 'dark';
  colorScheme: {
    primary: string | null;
    secondary: string | null;
  };
  url: string;
}

const title = 'Top 10 List | On3.com';
const description = `Check out On3.com's top 10 trending list at the current time.`;

const TopTenEmbed = ({ trendingList, mode, colorScheme, url }: TopTenProps) => {
  return (
    <EmbedWrapper description={description} mode={mode} title={title}>
      {colorScheme && (
        <Head>
          <style>{`:root { 
          --color-primary: ${'#' + colorScheme.primary}; 
          --color-secondary: ${'#' + colorScheme.secondary}; 
          }`}</style>
        </Head>
      )}
      {trendingList && <TopHeadlines data={trendingList} viewAllUrl={url} />}
    </EmbedWrapper>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const {
    id,
    limit,
    mode = 'light',
    primaryColor = '#fe3b1f',
    secondaryColor = '#f5f6f9',
    url = '/',
  } = ctx.query as {
    id: string;
    limit: string;
    mode?: 'light' | 'dark';
    primaryColor?: string | null;
    secondaryColor?: string | null;
    url?: string;
  };

  try {
    const listKey = getIdFromParam(id);

    if (!listKey) {
      return {
        notFound: true,
      };
    }

    const parsedLimit = Math.min(Number(limit) || 10, 10);

    const [trendingList] = await Promise.allSettled([
      rdbV1TrendingListsList(proxyApi, {
        trendingListKey: listKey,
        limit: parsedLimit,
      }),
    ]);

    return {
      props: {
        trendingList: getFulfilledValue(trendingList),
        mode,
        colorScheme: {
          primary: primaryColor,
          secondary: secondaryColor,
        },
        url,
      },
    };
  } catch (error) {
    console.error(error);

    return { props: {} };
  }
};

export default TopTenEmbed;
